import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ErrorBanner from '../components/ErrorBanner';
import StripeConnectionPanel from '../components/StripeConnectionPanel';
import { getAuthHeaders } from '../shared/api/authHeaders';

const uiConfig = require('../uiConfig');
const { getMessageForFetchError } = require('../shared/utils/apiError');

function StripeConnection() {
  const { userInfo, hasRole } = useAuth();
  const accountId = userInfo?.account_id || '';
  const isSuperAdmin = hasRole('Super Admin');
  const serverUrl = uiConfig.serverInfo.serverUrl;
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState(accountId);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function loadAccounts() {
      setLoading(true);
      setError('');
      try {
        const headers = await getAuthHeaders();
        const response = await fetch(`${serverUrl}/getAccount`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ account_id: accountId }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.message || data.error || 'Unable to load accounts.');
        }
        if (cancelled) return;

        const nextAccounts = Array.isArray(data) ? data : [];
        setAccounts(nextAccounts);
        const systemAccount = isSuperAdmin
          ? nextAccounts.find((account) => String(account.full_name || '').trim() === 'System')
          : null;
        const preferredId = systemAccount?._id || accountId || nextAccounts[0]?._id || '';
        setSelectedAccountId(String(preferredId));
      } catch (err) {
        if (!cancelled) setError(getMessageForFetchError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    if (accountId) {
      loadAccounts();
    } else {
      setLoading(false);
      setError('You must be signed in with an account to manage Stripe.');
    }

    return () => {
      cancelled = true;
    };
  }, [accountId, isSuperAdmin, serverUrl]);

  const selectedAccount = useMemo(
    () => accounts.find((account) => String(account._id) === String(selectedAccountId)) || null,
    [accounts, selectedAccountId]
  );

  return (
    <div className="page-container">
      <h1>Stripe Connection</h1>
      <ErrorBanner message={error} onDismiss={() => setError('')} />
      <p>
        Configure Stripe payment keys for an account. The account named <strong>System</strong>
        {' '}provides the default connection for accounts without their own Stripe connection.
      </p>

      {loading ? (
        <p>Loading…</p>
      ) : (
        <>
          {isSuperAdmin && accounts.length > 0 && (
            <div style={{ maxWidth: 520, marginBottom: 16 }}>
              <label className="panel-label" htmlFor="stripe-account-select">Account</label>
              <select
                id="stripe-account-select"
                className="input input-full"
                value={selectedAccountId}
                onChange={(event) => setSelectedAccountId(event.target.value)}
              >
                {accounts.map((account) => (
                  <option key={account._id} value={account._id}>
                    {account.full_name || account._id}
                    {String(account.full_name || '').trim() === 'System' ? ' (default)' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {selectedAccountId && (
            <StripeConnectionPanel
              accountId={selectedAccountId}
              title={`${selectedAccount?.full_name || 'Account'} Stripe connection`}
            />
          )}
        </>
      )}
    </div>
  );
}

export default StripeConnection;
